/**
 * Submission Triage list adapter.
 *
 * The list endpoint serves two row shapes behind `?view=` (see
 * `SubmissionViewItemWire` / `FacilityViewItemWire`). The table renders
 * from a single flat `TriageRow` so the cell renderer doesn't branch on
 * the view: `wireToTriageRow` maps either wire into it, leaving fields
 * the active view doesn't carry as `null` (rendered as "—" — backend
 * prevails, a null is a data gap, not a UI default).
 *
 * `rowKey` is the React/table key: the submission id in submission view,
 * the location id in facility view (where many rows share one
 * submission). `submissionId` is what row-click + the inline Status
 * Select act on (status lives at the submission level in both views).
 */
import type {
    FacilityViewItemWire,
    SubmissionStatus,
    SubmissionType,
    SubmissionViewItemWire,
    TriageItemWire,
    TriageViewParam,
    UserDisplay,
} from "../../types/submission";

export interface TriageRow {
    /** Unique table/React key (submission id or location id). */
    rowKey: string;
    /** Submission id — row navigation + inline Status PATCH target. */
    submissionId: string;

    // shared / UW
    title: string | null;
    facilityName: string | null;
    ccn: string | null;
    renewalDate: string | null;
    status: SubmissionStatus | null;
    type: SubmissionType | null;
    submissionDate: string | null;
    brokerage: string | null;
    brokerName: string | null;
    note: string | null;
    expiringPremium: number | null;
    expiringDeductible: number | null;
    hubspotId: string | null;

    // Risk Management Contact (submission view)
    rmName: string | null;
    rmPhone: string | null;
    rmEmail: string | null;

    // Schedule
    numFacilities: number | null;
    numSkilledBeds: number | null;
    numAssistedBeds: number | null;
    numIndependentBeds: number | null;
    numAdditionalBeds: number | null;
    retroDate: string | null; // pl_retro_date
    glRetroDate: string | null;

    // Schedule (facility view)
    facilityAddress: string | null;
    facilityCity: string | null;
    facilityState: string | null;
    facilityCounty: string | null;
    /** Absent from the wire until backend OR-1387 ships `schedule.zip_code`. */
    facilityZip: string | null;
    facilityGeo: string | null; // urbanicity
    licensedBeds: number | null;
    occupiedBeds: number | null;
    occupancyRatio: number | null;

    // Quote / Premium Selection (submission view)
    declaredSir: string | null;
    selectedPremium: string | null;
    triaFeeAmount: string | null;
    techRmFee: string | null;
    agentCommissionRate: string | null;
    tricuraCommissionRate: string | null;
    deviationFromUnity: string | null;
    probabilityToBind: string | null;
    policyNumber: string | null;

    // People
    assignee: UserDisplay | null;
    approver: UserDisplay | null;

    /** Open-comment count per commentable cell, keyed by the backend's
     *  dotted field path (`brokerage`, `quote.selected_premium`). Empty
     *  when the row has none or the view doesn't carry comments. */
    commentCounts: Record<string, number>;
}

/** Every TriageRow field defaults to null; each view fills its subset. */
function blankRow(submissionId: string, rowKey: string): TriageRow {
    return {
        rowKey,
        submissionId,
        title: null,
        facilityName: null,
        ccn: null,
        renewalDate: null,
        status: null,
        type: null,
        submissionDate: null,
        brokerage: null,
        brokerName: null,
        note: null,
        expiringPremium: null,
        expiringDeductible: null,
        hubspotId: null,
        rmName: null,
        rmPhone: null,
        rmEmail: null,
        numFacilities: null,
        numSkilledBeds: null,
        numAssistedBeds: null,
        numIndependentBeds: null,
        numAdditionalBeds: null,
        retroDate: null,
        glRetroDate: null,
        facilityAddress: null,
        facilityCity: null,
        facilityState: null,
        facilityCounty: null,
        facilityZip: null,
        facilityGeo: null,
        licensedBeds: null,
        occupiedBeds: null,
        occupancyRatio: null,
        declaredSir: null,
        selectedPremium: null,
        triaFeeAmount: null,
        techRmFee: null,
        agentCommissionRate: null,
        tricuraCommissionRate: null,
        deviationFromUnity: null,
        probabilityToBind: null,
        policyNumber: null,
        assignee: null,
        approver: null,
        commentCounts: {},
    };
}

function fromSubmissionView(item: SubmissionViewItemWire): TriageRow {
    const row = blankRow(item.id, item.id);
    row.title = item.title;
    row.renewalDate = item.renewal_date;
    row.status = item.status;
    row.type = item.type;
    row.submissionDate = item.submission_date;
    // The typed name covers submissions with no linked broker (OR-1789).
    row.brokerage = item.brokerage?.name ?? item.brokerage_name ?? null;
    row.note = item.note;
    row.expiringPremium = item.expiring_premium;
    row.expiringDeductible = item.expiring_deductible;
    row.hubspotId = item.hubspot_id;
    row.probabilityToBind = item.probability_to_bind;

    if (item.contact) {
        row.rmName = item.contact.risk_manager_name;
        row.rmPhone = item.contact.risk_manager_phone;
        row.rmEmail = item.contact.risk_manager_email;
    }
    if (item.schedule) {
        row.numFacilities = item.schedule.num_facilities;
        row.numSkilledBeds = item.schedule.num_skilled_beds;
        row.numAssistedBeds = item.schedule.num_assisted_beds;
        row.numIndependentBeds = item.schedule.num_independent_beds;
        row.numAdditionalBeds = item.schedule.num_additional_beds;
        row.retroDate = item.schedule.pl_retro_date;
    }
    if (item.quote) {
        row.declaredSir = item.quote.declared_sir;
        row.selectedPremium = item.quote.selected_premium;
        row.triaFeeAmount = item.quote.tria_fee_amount;
        row.techRmFee = item.quote.tech_rm_fee;
        row.agentCommissionRate = item.quote.agent_commission_rate;
        row.tricuraCommissionRate = item.quote.tricura_commission_rate;
        row.deviationFromUnity = item.quote.deviation_from_unity;
    }
    row.policyNumber = item.policy?.policy_number ?? null;
    row.assignee = item.assignee;
    row.approver = item.approver;
    for (const f of item.comments?.fields ?? []) {
        // A null field is a page-level thread — no cell to badge.
        if (f.field) row.commentCounts[f.field] = f.num_open_comments;
    }
    return row;
}

function fromFacilityView(item: FacilityViewItemWire): TriageRow {
    // Many facilities map to one submission — key on location_id so rows
    // stay unique, but navigate / PATCH status on the submission.
    const row = blankRow(item.submission_id, item.location_id);
    row.facilityName = item.facility?.name ?? null;
    row.ccn = item.cms_number;
    row.renewalDate = item.renewal_date;
    row.status = item.status;
    row.type = item.type;
    row.submissionDate = item.submission_date;
    row.brokerage = item.brokerage?.name ?? item.brokerage_name ?? null;
    row.brokerName = item.broker_name;

    if (item.schedule) {
        row.numSkilledBeds = item.schedule.num_skilled_beds;
        row.numAssistedBeds = item.schedule.num_assisted_beds;
        row.numIndependentBeds = item.schedule.num_independent_beds;
        row.numAdditionalBeds = item.schedule.num_additional_beds;
        row.facilityAddress = item.schedule.address;
        row.facilityCity = item.schedule.city;
        row.facilityState = item.schedule.state;
        row.facilityCounty = item.schedule.county;
        row.facilityZip = item.schedule.zip_code ?? null;
        row.facilityGeo = item.schedule.urbanicity;
        row.licensedBeds = item.schedule.num_licensed_beds;
        row.occupiedBeds = item.schedule.num_occupied_beds;
        row.occupancyRatio = item.schedule.occupancy_ratio;
        row.retroDate = item.schedule.pl_retro_date;
        row.glRetroDate = item.schedule.gl_retro_date;
    }
    row.policyNumber = item.policy?.policy_number ?? null;
    row.assignee = item.assignee;
    row.approver = item.approver;
    return row;
}

export function wireToTriageRow(item: TriageItemWire, view: TriageViewParam): TriageRow {
    return view === "facility"
        ? fromFacilityView(item as FacilityViewItemWire)
        : fromSubmissionView(item as SubmissionViewItemWire);
}

export function wireToTriageRows(items: TriageItemWire[], view: TriageViewParam): TriageRow[] {
    return items.map((item) => wireToTriageRow(item, view));
}
